import { useTheme } from "@emotion/react";
import SearchGradientIcon from 'ui-components/SearchGradientIcon'; // shared black search-field icon
import {
  CalendarMonthOutlined, CalendarTodayRounded, DescriptionOutlined,
  ExploreOutlined, FileUploadOutlined, FilterListRounded, HandshakeOutlined,
  RocketLaunchOutlined, Search, TrendingUpRounded, VolunteerActivismOutlined,
} from "@mui/icons-material";
import { Box, Button, Grid, Typography, useMediaQuery } from "@mui/material";
import BreadcrumbHeader from "layout/BreadcrumbsHeader";
import React from "react";
import { useNavigate } from "react-router-dom";
import NeedHelpButton from "ui-components/NeedHelpButton";
import NoReports from "./NoReports";
import ReportsSkeleton from "./ReportsSkeleton";
import ThemeBGWrapper from "ui-components/ThemeBGWrapper";
import ImageAvatar from "ui-components/ImageAvatar";
// Same detail popup the Deal Flow → startup → Reports tab uses, so both entry points
// open one identical, Figma-matching report popup instead of two different views.
import ReportDetailPopup from "../startup_applications/viewDetails/ReportDetailPopup";
// import { ReactComponent as MoreOptionsIcon } from "assets/rounds_icons/more_solid_icon.svg";
import CustomInputField from "ui-components/CustomInputField";
import DateRangeDialogPicker from "ui-components/DateRangeDialogPicker";
import { makeStyles } from "@mui/styles";
import Cookies from "js-cookie";
import { FetchAllReportsShared } from "api/report";
import { useSelector } from "react-redux";
import moment from "moment";
import { Primary } from 'ui-components/Buttons';



import FilterGradientIcon from 'ui-components/FilterGradientIcon';
/* ------------------------------------------------------------------ *
 * The report cards now run on the real API (`FetchAllReportsShared` ->
 * `reportsData`), mapped by `toCardData` below.
 *
 * Still placeholder, because no endpoint exists for them: the AI Updates
 * and Recent Activity panels. Their JSX is commented out in the render so
 * invented insights are not shown beside real reports - the consts and
 * components below are kept so switching them on is an uncomment.
 *
 * Also unavailable from the API, and so left off the card rather than
 * faked: the startup's sector / series, and a PDF page count.
 * ------------------------------------------------------------------ */

const PLACEHOLDER_AI_UPDATES = {
  range: 'May 20 – May 28, 2025',
  intro: "Here's what your portfolio companies have been up to based on their latest reports.",
  items: [
    { icon: TrendingUpRounded, title: 'Strong Revenue Momentum', text: '5 out of 8 startups reported revenue growth with an average increase of 28% this month.' },
    { icon: RocketLaunchOutlined, title: 'Product & Feature Launches', text: '6 startups launched new features or products to improve user engagement and retention.' },
    { icon: HandshakeOutlined, title: 'Partnerships & Expansion', text: '4 startups announced strategic partnerships and 2 expanded into new markets.' },
    { icon: ExploreOutlined, title: 'Looking Ahead', text: 'Most founders are focused on scaling operations, improving unit economics, and fundraising.' },
    { icon: VolunteerActivismOutlined, title: 'Fundraising Activity', text: '3 portfolio companies are actively raising their next round this quarter.' },
  ],
};

const PLACEHOLDER_ACTIVITY = [
  { name: 'Choose Your Path', action: 'submitted a new report', time: 'May 28, 2025 • 10:30 AM' },
  { name: 'EMAisha Pay', action: 'submitted a new report', time: 'May 28, 2025 • 09:15 AM' },
];

// 9, not 8: the cards run three-up on large screens, so 8 left the last row with a
// gap. 9 fills exactly three rows.
const REPORTS_PAGE_SIZE = 9;

// Maps a report from FetchAllReportsShared onto the card's fields.
//
// The API's own `select` already supplies name / image / documents / duration; the
// rest come straight from the backend's $project on investor-reports:
//   report_name (<- $name) · overview · from_date/to_date (+ formatted_*) ·
//   drive_info[{ filename, document_name }] · startup_name · createdAt
//
// Not available from any endpoint today, so deliberately left off the card rather
// than faked: the startup's sector/series, and a PDF page count. The document count
// replaces "N pages" because that is what the data actually describes.
// Same HTML→plain-text strip the Deal Flow report tab uses
// (views/startup_applications/viewDetails/Reports.js), so the overview reads as
// text on the card instead of showing raw <p>/<strong> tags.
const stripHtml = (html) =>
  String(html ?? '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const toCardData = (report) => {
  const documents = report?.documents || [];
  const period = [report?.formatted_from_date, report?.formatted_to_date].filter(Boolean).join(' – ');
  const firstFile = documents[0]?.filename || '';
  const extension = firstFile.includes('.') ? firstFile.split('.').pop() : '';

  return {
    ...report,
    title: report?.report_name || '',
    description: stripHtml(report?.overview),
    date: period || (report?.createdAt ? moment(report.createdAt).format('DD-MMM-YYYY') : ''),
    docCount: documents.length,
    fileType: extension ? extension.toUpperCase() : '',
  };
};

// Figma-measured tokens (flagged: read off the design image, not exported values)
const HEADING = '#101828';
const MUTED = '#667085';
const DESC = '#98A2B3';
const CARD_BORDER = '#EAECF0';
const ICON_BG = '#EEF4FF';
const ACCENT = '#5046E5';

const cardSx = {
  bgcolor: '#fff',
  border: `1px solid ${CARD_BORDER}`,
  borderRadius: '12px',
  p: 2,
};

const ReportCard = ({ data, onOpen }) => (
  <Box
    onClick={onOpen}
    sx={{
      ...cardSx,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      gap: 1.25,
      cursor: 'pointer',
      transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
      '&:hover': { boxShadow: '0px 4px 16px rgba(16,24,40,0.06)', borderColor: '#C7D7FE' },
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
      {/* Initials + tint come from the name via ImageAvatar/avatarColors, so the same
          startup keeps the same swatch everywhere. The card's own 36px/8px sizing is
          kept, overriding the component's 2.5rem/9px default. */}
      <ImageAvatar
        src={data?.image}
        alt={data?.name}
        width={36}
        height={36}
        style={{ width: 36, height: 36, borderRadius: '8px', fontSize: '0.85rem', fontWeight: 700 }}
      />
      <Box sx={{ minWidth: 0 }}>
        <Typography noWrap sx={{ fontWeight: 600, fontSize: '0.85rem', color: HEADING }}>{data?.name}</Typography>
        {/* sector/series have no API yet, so this line only renders if they ever arrive -
            an always-empty Typography would leave a gap under every name. */}
        {[data?.sector, data?.series].filter(Boolean).length > 0 && (
          <Typography noWrap sx={{ fontSize: '0.72rem', color: MUTED }}>
            {[data?.sector, data?.series].filter(Boolean).join(' • ')}
          </Typography>
        )}
      </Box>
    </Box>
    {data?.title && (
      <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: HEADING }}>{data.title}</Typography>
    )}
    {data?.description && (
      <Typography
        sx={{
          fontSize: '0.78rem', color: DESC, lineHeight: 1.5,
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
        }}
      >
        {data.description}
      </Typography>
    )}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 'auto', pt: 0.5, minWidth: 0 }}>
      {data?.date && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
          <CalendarMonthOutlined sx={{ fontSize: 14, color: MUTED }} />
          <Typography noWrap sx={{ fontSize: '0.72rem', color: MUTED }}>{data.date}</Typography>
        </Box>
      )}
      {/* Document count, not a PDF page count - that is what drive_info actually gives us. */}
      {data?.docCount > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <DescriptionOutlined sx={{ fontSize: 14, color: MUTED }} />
          <Typography noWrap sx={{ fontSize: '0.72rem', color: MUTED }}>
            {data.docCount} {data.docCount === 1 ? 'document' : 'documents'}
          </Typography>
        </Box>
      )}
      {data?.fileType && (
        <Typography sx={{ ml: 'auto', fontWeight: 700, fontSize: '0.72rem', color: HEADING }}>{data.fileType}</Typography>
      )}
    </Box>
  </Box>
);

const PanelIconTile = ({ Icon }) => (
  <Box sx={{ width: 36, height: 36, borderRadius: '8px', bgcolor: ICON_BG, color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
    <Icon sx={{ fontSize: 18 }} />
  </Box>
);

const AIUpdatesPanel = ({ data }) => (
  <Box sx={{ ...cardSx, p: 2.5 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
      <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: HEADING }}>AI Updates</Typography>
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, bgcolor: ICON_BG, borderRadius: '6px', px: 1, py: '4px' }}>
        <CalendarTodayRounded sx={{ fontSize: 12, color: ACCENT }} />
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: ACCENT }}>{data.range}</Typography>
      </Box>
    </Box>
    <Typography sx={{ fontSize: '0.78rem', color: '#475467', lineHeight: 1.5, mb: 2 }}>{data.intro}</Typography>
    {data.items.map((item, i) => (
      <Box
        key={item.title}
        sx={{
          display: 'flex', gap: 1.25, py: 1.5,
          borderTop: i === 0 ? 'none' : `1px solid ${CARD_BORDER}`,
        }}
      >
        <PanelIconTile Icon={item.icon} />
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: HEADING, mb: 0.25 }}>{item.title}</Typography>
          <Typography sx={{ fontSize: '0.75rem', color: MUTED, lineHeight: 1.5 }}>{item.text}</Typography>
        </Box>
      </Box>
    ))}
  </Box>
);

const RecentActivityPanel = ({ data, sx = {} }) => (
  <Box sx={{ ...cardSx, p: 2.5, ...sx }}>
    <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: HEADING, mb: 1.5 }}>Recent Activity</Typography>
    {data.map((item, i) => (
      <Box key={i} sx={{ display: 'flex', gap: 1.25, py: 1.25 }}>
        <PanelIconTile Icon={FileUploadOutlined} />
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.8rem', color: HEADING, mb: 0.25 }}>
            <Box component="span" sx={{ fontWeight: 700 }}>{item.name}</Box>{' '}{item.action}
          </Typography>
          <Typography sx={{ fontSize: '0.72rem', color: MUTED }}>{item.time}</Typography>
        </Box>
      </Box>
    ))}
  </Box>
);

const ReportsPagination = ({ page, pageCount, onChange, sx = {} }) => {
  if (pageCount <= 1) return null;
  const btnSx = {
    minWidth: 32, height: 32, p: 0, borderRadius: '8px',
    border: `1px solid ${CARD_BORDER}`, bgcolor: '#fff', color: MUTED,
  };
  return (
    /* flexWrap so a long page list wraps onto a second line instead of overflowing -
       7+ pages of 32px buttons is wider than a phone. */
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: 1, mt: 3, ...sx }}>
      <Button sx={btnSx} disabled={page === 1} onClick={() => onChange(page - 1)}>‹</Button>
      {Array.from({ length: pageCount }, (_, i) => i + 1).map((p) => (
        <Button
          key={p}
          onClick={() => onChange(p)}
          sx={{
            ...btnSx,
            ...(p === page && { bgcolor: ICON_BG, borderColor: ICON_BG, color: ACCENT, fontWeight: 700 }),
          }}
        >
          {p}
        </Button>
      ))}
      <Button sx={btnSx} disabled={page === pageCount} onClick={() => onChange(page + 1)}>›</Button>
    </Box>
  );
};

// const reportData = [
//   {
//     report_name: 'MIS Q1 24',
//     name: 'Redesyn',
//     image: "https://startups-prod-pub.s3.ap-south-1.amazonaws.com/redesyn/logo.jpeg",
//     email: 'puneetmadaan369@gmail.com',
//     duration: '01-Mar-2024 - 30-Jun-2024',
//     overview: 'Here is the MIS report of quarter 1 of 2024',
//   },
//   {
//     report_name: 'MIS Q2 2024',
//     name: 'Redesyn',
//     image: 'https://startups-prod-pub.s3.ap-south-1.amazonaws.com/redesyn/logo.jpeg',
//     email: 'puneetmadaan369@gmail.com',
//     duration: '30-Jul-2024 - 29-Sep-2024',
//     overview: 'Here is the MIS report of quarter 1 of 2024',
//   }
// ];

const useStyles = makeStyles((theme) => ({
  scrollTabs: {
    '& > .Mui-disabled.MuiTabs-scrollButtons': {
      display: 'none',
      transition: 'all 1s ease'
    },
  },
  // The `DateRangePicker` rules that lived here styled rsuite's .rs-picker-*
  // internals. That picker is gone from this page (replaced by the shared
  // DateRangeDialogPicker), so the selectors matched nothing.
}));






const Reports = () => {
  const theme = useTheme();
  const classes = useStyles();
  const isNotLargeScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [search, setSearch] = React.useState("");
  const [reset, setReset] = React.useState(false);
  const [investor_id,setInvestorID] = React.useState(Cookies.get("X-INV-ID") || null)

  const navigate = useNavigate();
  const userDetails = useSelector((state) => state?.investorDetails?.data);
  const [duration, setDuration] = React.useState(null)
  const { data: reportsData, isLoading: isLoadingReports, refetch: refetchReportsData } = FetchAllReportsShared({ investor_id, fromDate: duration?.from_date, toDate: duration?.to_date, search });
  console.log(reportsData,'reportsData');

  //   const Actions  = () => {
  //     return (
  //       <Primary onClick={() => navigate("/portfolio/create_portfolio")} sx={{ padding: '10px 16px', fontWeight: 600, minWidth: isSmallScreen ? "0" : '64px', '& > .MuiButton-icon.MuiButton-startIcon': { marginRight: isSmallScreen ? 0 : '8px', marginLeft: isSmallScreen ? 0 : '-4px' } }} startIcon={<Add fontSize='1.25rem' />}>
  //         {!isSmallScreen ? "Add Portfolio" : ''}
  //       </Primary>
  //     )
  //   }

  const [searchStatus, setSearchStatus] = React.useState(false);

  const refetchListing = () => {
    // console.log(search, searchStatus, duration, "ddddd")
    refetchReportsData()
    // console.log(search, searchStatus, duration, "ddddd")
    // console.log("refetchListing")
  }

  // The chosen duration is not part of the query key, so picking a range has to ask for the
  // refetch. The popover's Apply button used to do this; the dialog's own Apply now
  // lands in onChange, and this effect follows it.
  React.useEffect(() => {
    refetchListing();
  }, [reset, duration])

  // Opening a report now shows the shared ReportDetailPopup (same as Deal Flow),
  // instead of navigating to the /reports/view page.
  const [selectedReport, setSelectedReport] = React.useState(null);
  const openReport = (report) => {
    if (!report?._id) return; // placeholder rows have no real id
    setSelectedReport(report);
  };

  const [page, setPage] = React.useState(1);
  const [filterOpen, setFilterOpen] = React.useState(false);

  // 🔴 PLACEHOLDER: swap to `reportsData` to wire the real API (see the const block above).
  const list = reportsData;

  const pageCount = Math.max(1, Math.ceil((list?.length || 0) / REPORTS_PAGE_SIZE));
  const pagedReports = React.useMemo(
    () => (list || []).slice((page - 1) * REPORTS_PAGE_SIZE, page * REPORTS_PAGE_SIZE),
    [list, page]
  );

  return (

    // ThemeBGWrapper is position:fixed. This page turns it into a column: everything
    // scrolls in the middle, and the pagination footer below locks its own strip of space.
    // Scoped to this page via the sx prop; the shared component is untouched.
    // height: the wrapper is position:fixed with top:20px and height:100%, which resolves
    // against the viewport - so it ends 20px below the fold and clipped the footer. Pinned
    // to the same calc as its own minHeight so the strip lands on-screen. Scoped via sx.
    <ThemeBGWrapper sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', height: { xs: '100dvh', md: 'calc(100dvh - 1.25rem)' } }}>
      {/* Header sits outside the scroller as a flex sibling, so it holds its own strip and
          never moves - no sticky/z-index/background needed to keep content from showing
          through it. */}
      <Box sx={{ flexShrink: 0 }}>
        <BreadcrumbHeader
          title={"Reports"}
          breadcrumbTitle="Reports"
          customBackgroundColor={theme.palette.customColors.indigo25}
          // Actions={Actions}
          sx={{ paddingBottom: "0.5rem" }}
          Actions={() => <NeedHelpButton />}
        />
      </Box>

      {/* The scroller between the pinned header and the pinned footer. minHeight:0 lets it
          shrink inside the flex column instead of pushing the footer off-screen. */}
      {/* overflowX:'hidden' - MUI spacing containers are laid out with a negative left
          margin (-20px for spacing 2.5), which is wider than this page's 1rem phone
          padding and left the whole page scrollable sideways by a few pixels. Nothing
          here is meant to be wider than the viewport, so clip it. */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
      {/* 2rem each side takes ~64px off a 360px phone, which squeezes the full-width
          report cards. Halved below md. */}
      <Grid item xs={12} container padding={{ xs: "0rem 1rem 1rem 1rem", md: "0rem 2rem 2rem 2rem" }}>
        {/* ---- Top bar: All Reports (N) + search + Filter ---- */}
        {/* Sticky to the top of the scroller. The gap below it is padding, not margin, so
            the #fffdfa background covers it and cards slide under cleanly instead of
            appearing in a transparent strip. */}
        <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap', pb: 2, position: 'sticky', top: 0, zIndex: 3, backgroundColor: '#fffdfa' }}>
          <Typography sx={{  display: {xs: "none", sm: "block"} ,fontWeight: 700, fontSize: '1rem', color: HEADING }}>
            All Reports ({list?.length || 0})
          </Typography>
          {/* alignItems:'stretch', not 'center': CustomInputField renders an outer Box
              wrapper, so the field's height is whatever that wrapper computes to. With
              'center' the filter button sized to its own content and ended up shorter
              than the input. Stretching the row makes both items take the line height -
              i.e. the input's - so they match by construction, with no magic number to
              drift out of sync. */}
          {/* width:100% on xs so this drops onto its own line under the count instead of
              fighting the title for the leftover sliver - that left the search field a few
              characters wide ("Search b...") on a phone. minWidth:0 lets it shrink from sm
              up rather than pushing the row past the viewport. */}
          <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 1.5, flexWrap: 'nowrap', width: { xs: '100%', sm: 'auto' }, minWidth: 0 }}>
            <CustomInputField
              autoComplete="off"
              sx={{
                marginBottom: 0,
                width: { xs: '100%', sm: 320 },
                "& > .MuiFilledInput-root": {
                  borderColor: `${CARD_BORDER} !important`,
                  borderRadius: '10px',
                  backgroundColor: '#fff',
                }
              }}
              fullWidth
              value={search}
              name={`search_${Math.random().toString(36).substring(7)}`}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  refetchListing()
                  if (search) {
                    setSearchStatus(true)
                  }
                  setPage(1);
                }
              }}
              onChange={(event) => {
                setSearch(event.target.value)
                setSearchStatus(false)
              }}
              size="small"
              startAdornment={<SearchGradientIcon />}
              placeholder={'Search by startup/report name'}
            />
            {/* Figma shows a "Filter" button. The existing date-range filter is real
                (it feeds FetchAllReportsShared), so it lives inside this popover
                rather than being dropped. */}
            <Button
              onClick={() => setFilterOpen(true)}
              sx={{
                minWidth: 42,
                flexShrink: 0,
                // Height comes from the row's alignItems:'stretch' (see the comment on
                // the wrapper Box). No alignSelf and no fixed height here - both were
                // tried and both left this shorter than the search field.
                px: 2,
                py: 0,
                borderRadius: '10px',
                border: `1px solid ${duration ? ACCENT : CARD_BORDER}`,
                bgcolor: '#fff',
                color: duration ? ACCENT : HEADING,
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': { bgcolor: '#fff', borderColor: ACCENT },
              }}
            >
              <FilterGradientIcon active={!!duration} />
            </Button>
            {/* One dialog, not two. The Filter icon used to open a popover that only held
                a label plus this picker's own trigger - and that trigger opened the real
                date dialog, which already has its own Clear / Apply. The icon now opens
                that dialog directly; the picker keeps its trigger hidden here. */}
            <DateRangeDialogPicker
              value={duration}
              open={filterOpen}
              onOpenChange={setFilterOpen}
              hideTrigger
              dialogTitle="Filter by date range"
              displayFormat="DD-MMM-YYYY"
              onChange={(value) => {
                setDuration(value);
                setSearchStatus(false);
                setPage(1);
              }}
            />
          </Box>
        </Grid>

        {isLoadingReports ? (
          <Grid item xs={12}>
            <ReportsSkeleton />
          </Grid>
        ) : (
          // stretch, not flex-start: the Figma lines the two columns up on the same bottom
          // edge, so the right column takes the row's height and Recent Activity grows into
          // the slack below AI Updates.

          <Grid item xs={12} container alignItems="stretch">
            {/* ---- Left: report cards ---- */}
            {/* The cards scroll in their own box. flex:1 + minHeight:0 makes that box fill
                the column's full height rather than sitting at a fixed cap, so the cards run
                to the bottom and no blank strip appears beneath them. */}
            {/* Full width while AI Updates / Recent Activity are parked - three cards per
                row on large screens so they stay a readable width rather than stretching. */}
            <Grid item xs={12} lg={12} sx={{ display: 'flex', flexDirection: 'column' }}>
              {pagedReports?.length > 0 ? (
                <Box
                  sx={{
                    flex: { lg: 1 },
                    minHeight: 0,
                    overflowY: { xs: 'visible', lg: 'auto' },
                    pr: { lg: 0.5 },
                  }}
                >
                  <Grid container spacing={2}>
                    {pagedReports.map((report) => (
                      <Grid item xs={12} sm={6} lg={4} key={report?._id || report?.id}>
                        <ReportCard data={toCardData(report)} onOpen={() => openReport(report)} />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              ) : (
                /* On a phone the 48px card padding plus NoData's 60dvh made the empty
                   card taller than the viewport, so the folder mark floated in a huge
                   blank box. Both shrink below md. */
                <Box sx={{ ...cardSx, py: { xs: 2, md: 6 } }}>
                  <NoReports height={isSmallScreen ? '32dvh' : undefined} />
                </Box>
              )}
            </Grid>

            {/* ---- Right: AI Updates + Recent Activity ----
                Parked until there is an API behind them - both were placeholder-only, and
                showing invented insights next to real reports reads as real data. The
                panels, their components and their placeholder consts are all still here,
                so switching them back on is uncommenting this block.

            <Grid item xs={12} lg={4.5} sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <AIUpdatesPanel data={PLACEHOLDER_AI_UPDATES} />
              <RecentActivityPanel data={PLACEHOLDER_ACTIVITY} sx={{ flex: { lg: 1 } }} />
            </Grid>
            */}
          </Grid>
        )}
      </Grid>
      </Box>

      {/* Locked footer: its own strip of space at the bottom of the page. It is a sibling
          of the scroller, not inside it, so it never moves and nothing scrolls past it. */}
      {pageCount > 1 && !isLoadingReports && (
        <Box
          sx={{
            flexShrink: 0,
            backgroundColor: '#fffdfa',
            borderTop: `1px solid ${CARD_BORDER}`,
            px: { xs: '1rem', md: '2rem' },
            // The Figma gives the pagination real breathing room rather than sitting it on
            // the page edge, so the strip holds its own space.
            py: 2,
          }}
        >
          <ReportsPagination page={page} pageCount={pageCount} onChange={setPage} sx={{ mt: 0 }} />
        </Box>
      )}

      {/* Shared report detail popup - identical to the Deal Flow startup Reports tab. */}
      {selectedReport && (
        <ReportDetailPopup report={selectedReport} onClose={() => setSelectedReport(null)} />
      )}
    </ThemeBGWrapper>
  );
};

export default Reports;
